import {
  I3DCallbacks,
  I3DActionRequest,
  I3DActionResponse,
  IssueMarkerInfo,
} from '@trimble/tc-issue-management-ui';
import { TcRegion, TcTcpsApiService } from '@data-access/connect/tc-tcps';
import { getViewData, ProjectDataState } from '@mobile-mapping/providers';
import {
  hasModelObjectSelection,
  getSelectedObjectLinkData,
  getSelectedSourceIds,
  selectLinkedObjects,
} from '@mobile-mapping/web3d';
import {
  IssueMarkerIconCoordinator,
  IssueMarkerPickCoordinator,
} from '@trimble/tc-issue-management-web3d-utilities';
import { ILogger } from '@trimble/logging';
import { CapabilityRegistry } from '@trimble/web3d';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import { CreateViewRequest } from '@data-access/connect/tc-tcps';
import { useLogger } from '@trimble/solidjs-utils';

function logger(): ILogger {
  return useLogger().getLogger('IssueManagementCbStore');
}

export function IssueManagementCbStore(
  projectId: string,
  capabilityRegistry: CapabilityRegistry,
  dataState: ProjectDataState,
  region: TcRegion,
  tcTcpsApi: TcTcpsApiService,
  viewsProvider: ViewsCapabilityProvider
): I3DCallbacks {
  const handleAddIssueView = async (
    request: Omit<I3DActionRequest, 'payload'>
  ): Promise<
    Omit<I3DActionResponse, 'data'> & { data: CreateViewRequest | null }
  > => {
    const { connectCamera, imageData, modelVersionIds, markups } =
      await getViewData(dataState, capabilityRegistry);
    return {
      type: request.type,
      success: true,
      message: 'View data captured successfully',
      data: {
        name: `View for Issue ${request.issueId}`,
        projectId: projectId,
        issueId: request.issueId,
        contextId: request.contextId,
        models: modelVersionIds,
        camera: connectCamera,
        markups: markups.length > 0 ? markups : undefined,
        imageData,
      },
    };
  };

  const handleAddIssueCommentView = async (
    request: I3DActionRequest
  ): Promise<
    Omit<I3DActionResponse, 'data'> & { data: CreateViewRequest | null }
  > => {
    try {
      const { connectCamera, imageData, modelVersionIds, markups } =
        await getViewData(dataState, capabilityRegistry);
      return {
        type: request.type,
        success: true,
        message: 'Comment viewpoint captured successfully',
        data: {
          name: `Comment View for Issue ${request.issueId}`,
          projectId: projectId,
          issueId: request.issueId,
          contextId: request.contextId,
          models: modelVersionIds,
          camera: connectCamera,
          markups: markups.length > 0 ? markups : undefined,
          imageData,
        },
      };
    } catch (error) {
      logger().error('Failed to capture comment viewpoint', { error });
      return {
        type: request.type,
        success: false,
        message: 'Failed to capture comment viewpoint',
        data: null,
      };
    }
  };

  const handleIssueViewSelect = async (
    request: Omit<I3DActionRequest, 'payload'> & {
      payload?: { viewId: string };
    }
  ): Promise<I3DActionResponse> => {
    if (!request.payload?.viewId) {
      throw new Error('viewId undefined');
    }

    const view = await tcTcpsApi.getViewDetails(
      request.payload?.viewId,
      region
    );

    await viewsProvider.loadView(view, region);

    return {
      type: request.type,
      success: true,
      message: 'View loaded successfully',
    };
  };

  const iconCoordinator = capabilityRegistry.get(IssueMarkerIconCoordinator)();
  const pickCoordinator = capabilityRegistry.get(IssueMarkerPickCoordinator)();

  const handleIssueItemSelect = (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    const issue = request.payload?.issue;
    iconCoordinator?.setSelectedMarkerId(issue?.id ?? null);

    return Promise.resolve({
      type: request.type,
      success: true,
      message: 'Issue item selected',
    });
  };

  const handleMarkersChange = (
    markers: readonly IssueMarkerInfo[],
    _showAll?: boolean
  ): void => {
    iconCoordinator?.setMarkers(markers);
  };

  const handleRemoveIssueMarker = (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    pickCoordinator?.cancelPick();
    iconCoordinator?.removeMarker();

    return Promise.resolve({
      type: request.type,
      success: true,
      message: 'Marker removed successfully',
      data: { marker: null },
    });
  };

  const handleAddIssueMarker = async (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    pickCoordinator?.cancelPick();

    const point = await pickCoordinator?.pickMarkerPosition();
    if (!point) {
      return {
        type: request.type,
        success: false,
        message: 'Marker placement failed or cancelled',
      };
    }

    iconCoordinator?.showMarker(point, {
      markerType: request.payload?.issueType as string | undefined,
      priority: request.payload?.priority as string | undefined,
    });

    return {
      type: request.type,
      success: true,
      message: 'Marker position captured',
      data: {
        marker: { point },
      },
    };
  };

  const handleViewIssueModelObjects = async (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    try {
      const links = await tcTcpsApi.listLinksForTarget(
        region,
        request.issueId,
        'ISSUE',
        projectId,
        request.contextId ?? ''
      );

      const elements = links.flatMap((link) =>
        link.source.data.map((d) => ({
          versionId: link.source.versionId,
          sourceId: d.sourceId,
        }))
      );

      if (elements.length === 0) {
        return {
          type: request.type,
          success: false,
          message: 'No linked model objects found for this issue',
        };
      }

      const anySelected = await selectLinkedObjects(
        capabilityRegistry,
        elements,
        dataState
      );

      return {
        type: request.type,
        success: anySelected,
        message: anySelected
          ? `Selected ${String(elements.length)} linked object(s) in the viewer`
          : 'Linked objects not found in any loaded model',
      };
    } catch (error) {
      logger().error('Failed to view issue model objects', {
        error,
      });
      return {
        type: request.type,
        success: false,
        message: 'Failed to fetch linked model objects',
      };
    }
  };

  const handleLinkIssueModelObjects = async (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    if (!hasModelObjectSelection(capabilityRegistry)) {
      return {
        type: request.type,
        success: false,
        message: 'No model objects selected in the viewer',
      };
    }

    try {
      const linkData = await getSelectedObjectLinkData(
        capabilityRegistry,
        request.issueId,
        request.contextId ?? '',
        dataState
      );

      if (!linkData) {
        return {
          type: request.type,
          success: false,
          message: 'Could not resolve selected objects to linkable data',
        };
      }

      return {
        type: request.type,
        success: true,
        message: 'Object link data captured from viewer selection',
        data: linkData,
      };
    } catch (error) {
      logger().error('Failed to link issue model objects', { error });
      return {
        type: request.type,
        success: false,
        message: 'Failed to capture object link data from viewer selection',
      };
    }
  };

  const handleUnlinkIssueModelObjects = async (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    if (!hasModelObjectSelection(capabilityRegistry)) {
      return {
        type: request.type,
        success: false,
        message: 'No model objects selected in the viewer',
      };
    }

    try {
      const selectedSourceIds = await getSelectedSourceIds(capabilityRegistry);
      if (selectedSourceIds.length === 0) {
        return {
          type: request.type,
          success: false,
          message: 'Could not resolve selected objects to identifiers',
        };
      }

      const links = await tcTcpsApi.listLinksForTarget(
        region,
        request.issueId,
        'ISSUE',
        projectId,
        request.contextId ?? ''
      );

      const selectedSet = new Set(selectedSourceIds);
      const matchingLink = links.find((link) =>
        link.source.data.some((d) => selectedSet.has(d.sourceId))
      );

      if (!matchingLink) {
        return {
          type: request.type,
          success: false,
          message: 'Selected objects are not linked to this issue',
        };
      }

      return {
        type: request.type,
        success: true,
        message: 'Link identified for selected objects',
        data: { linkId: matchingLink.id },
      };
    } catch (error) {
      logger().error('Failed to unlink issue model objects', { error });
      return {
        type: request.type,
        success: false,
        message: 'Failed to identify link for selected objects',
      };
    }
  };

  const handleUnlinkAllIssueModelObjects = async (
    request: I3DActionRequest
  ): Promise<I3DActionResponse> => {
    try {
      const links = await tcTcpsApi.listLinksForTarget(
        region,
        request.issueId,
        'ISSUE',
        projectId,
        request.contextId ?? ''
      );

      if (links.length === 0) {
        return {
          type: request.type,
          success: true,
          message: 'No linked model objects to remove',
        };
      }

      await Promise.all(
        links.map((link) => tcTcpsApi.deleteObjectLink(region, link.id))
      );

      return {
        type: request.type,
        success: true,
        message: `Removed ${String(links.length)} object link(s)`,
      };
    } catch (error) {
      logger().error('Failed to unlink all issue model objects', { error });
      return {
        type: request.type,
        success: false,
        message: 'Failed to remove all object links',
      };
    }
  };

  return {
    onAddIssueView: handleAddIssueView,
    onViewIssueModelObjects: handleViewIssueModelObjects,
    onLinkIssueModelObjects: handleLinkIssueModelObjects,
    onUnlinkIssueModelObjects: handleUnlinkIssueModelObjects,
    onUnlinkAllIssueModelObjects: handleUnlinkAllIssueModelObjects,
    onIssueItemSelect: handleIssueItemSelect,
    onRemoveIssueMarker: handleRemoveIssueMarker,
    onIssueViewSelect: handleIssueViewSelect,
    onAddIssueCommentView: handleAddIssueCommentView,
    onAddIssueMarker: handleAddIssueMarker,
    onMarkersChange: handleMarkersChange,
  };
}
